import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { chain } from 'lodash';

import Speed from 'components/shared/speed';
import Altitude from 'components/shared/altitude';
import Course from 'components/shared/course';
import DateTime from 'components/shared/datetime';
import Coordinate from 'components/shared/coordinate';
import Icon from 'components/shared/icons/eventIcons';
import { isSignificantEvent, labelToDisplayLabel } from 'helpers/events';
import useTranslation from 'hooks/useTranslation';
import moment from 'moment';
import reports from 'repositories/reports';
import { gatewayToTransport } from 'helpers/transport';
import clsx from 'clsx';
import DirectionIcon from 'components/shared/icons/directionIcon';
import TransmissionIcon from 'components/shared/icons/transmissionIcon';
import useStyles from './reportDetails-styles';

// Renders the given field as a clean 2-row control with label / value.
// Optionally renders children instead of value (if a more complex control is required)
const DetailField = ({
  label,
  value,
  className,
  fullWidth,
  children
}) => (
  <Grid className={className} item xs={12} sm={fullWidth ? 12 : 6}>
    <Typography>{label}</Typography>
    {children || <Typography>{value}</Typography>}
  </Grid>
);

// Renders the given data object as a 2-column array of <DetailField>
const Simple2dGrid = ({
  data,
  containerClassName,
  itemClassName
}) => {
  const keys = Object.keys(data);

  return chain(keys).groupBy(fieldName => Math.floor(keys.indexOf(fieldName) / 2)).map((row, key) => (
    /* eslint-disable-next-line react/no-array-index-key *///* this is a lodash map over a dictionary not an array
    <Grid key={key} className={containerClassName} container spacing={0}>
      {row.map(fieldName => (
        <DetailField
          key={fieldName}
          className={itemClassName}
          label={fieldName}
          value={data[fieldName]}
        />
      ))}
    </Grid>
  )).value();
};

const EventIcon = ({ label, className }) => (
  <Box className={className}>
    <Icon type={label} />
  </Box>
);

// Displays information common to all reports, eg: the Event name, icon, transport etc
const Header = ({
  classes,
  t,
  asset,
  report
}) => (
  <Box className={classes.detailsPanelHeader}>
    <Grid container spacing={0} alignItems="center" className={classes.title}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h3">{asset.name}</Typography>
      </Grid>
      <Grid className={classes.flexWrapper} item xs={12} sm={6}>
        {report?.gateway && (
          <Box className={classes.eventIcon}>
            <TransmissionIcon transport={gatewayToTransport(report.gateway)} fillColor="rgba(0,0,0,0.5)" circle />
          </Box>
        )}
        {report?.events && report.events.map((e, i) => isSignificantEvent(e)
          && <EventIcon key={i} label={e} className={classes.eventIcon} />)}
        <Box className={classes.iconWrapper}>
          <DirectionIcon style={{ transition: 'all 0.15s', transform: `rotate(${report?.course || '0'}deg)` }} fillColor="rgba(0,0,0,0.5)" circle />
        </Box>
      </Grid>
    </Grid>
    <Grid container spacing={0} alignItems="center">
      <DetailField className={classes.gridItem} label={t('transport')} value={gatewayToTransport(report?.gateway) || '--'} />
      <DetailField className={classes.gridItem} label={t('eventType')} value={labelToDisplayLabel(report?.events?.[0]) || t('standard')} />
    </Grid>
  </Box>
);

const PositionDetails = ({
  classes,
  t,
  position
}) => (
  <>
    <Grid className={classes.gridContainer} container spacing={0}>
      <DetailField label={t('timeOfFix')} fullWidth>
        <DateTime value={moment.unix(position.received)} />
      </DetailField>
    </Grid>

    <Grid className={classes.gridContainer} container spacing={0}>
      <DetailField label="Coordinates" fullWidth>
        <Coordinate latitude={position.latitude} longitude={position.longitude} />
      </DetailField>
    </Grid>

    <Grid className={classes.gridContainer} container spacing={0}>
      <DetailField className={classes.gridItem} label={t('altitude')}>
        <Altitude altitudeInMetres={position.altitude} precision={0} />
      </DetailField>
    </Grid>

    <Grid className={classes.gridContainer} container spacing={0}>
      <DetailField className={classes.gridItem} label={t('speed')}>
        <Speed speedInMetersPerSecond={position.speed} />
      </DetailField>
      <DetailField className={classes.gridItem} label={t('course')}>
        <Course courseInDegreesTrue={position.course} timestamp={position.received} position={position} />
      </DetailField>
    </Grid>
  </>
);

const ReportDetailsOverlay = ({
  selectedReport,
  asset,
  highContrastControls,
  setReportDetailsPanelWidth
}) => {
  const classes = useStyles();
  const t = useTranslation('pages.map.reportDetails');
  const reportPanelRef = useRef(null);
  useEffect(() => {
    setReportDetailsPanelWidth(reportPanelRef?.current?.clientWidth ?? 0);
  }, [reportPanelRef?.current?.clientWidth, setReportDetailsPanelWidth]);

  const report = useCallback(() => reports.getReport(selectedReport?.id), [selectedReport])();

  if (!report || !report.isValid || !asset) return null;

  return (
    <Box className={clsx(classes.detailsPanel, { [classes.highContrastBackground]: highContrastControls })} ref={reportPanelRef}>
      <Header classes={classes} t={t} asset={asset} report={report} />

      <Box className={classes.detailsPanelBody}>
        <PositionDetails classes={classes} t={t} position={report} />

        {report?.metadata && (
          <Simple2dGrid
            data={report.metadata}
            containerClassName={classes.gridContainer}
            itemClassName={classes.gridItem}
          />
        )}

        {/*
        {report?.form?.dataJson && (
          <Simple2dGrid
            data={JSON.parse(report.form.dataJson)}
            containerClassName={classes.gridContainer}
            itemClassName={classes.gridItem}
          />
        )}
        */}
      </Box>
    </Box>
  );
};

export default ReportDetailsOverlay;
